<template>
  <UnifiedFilterBanner mode="reviews" :is-loading="isLoading" :permalink="props.permalink" :initial-state="props.state"
    :initial-city="props.city" :initial-role="initialRoleForBanner"
    :pre-fetched-cities="preFetchedCities" :states-cities="statesCities" @filter="handleFilter" />
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
    <SectionHeader :title="titleText">
      <template #action>
        <ActionButton v-if="!showWriteReview" :text="userReview ? 'Update my Review' : 'Write a Review'"
          icon="i-heroicons-plus" :brand-color="props.brand_color" @click="handleWriteReview" />
      </template>
    </SectionHeader>
    <div class="flex">
      <LastUpdatedDate section="reviews" />
    </div>
    <div v-if="showWriteReview && !userReview" ref="writeReviewFormContainer" class="mb-6">
      <WriteReviewForm ref="writeReviewForm" :is-submitting="isSubmitting" :initial-data="reviewFormData"
        :is-update="false" :permalink="props.permalink" @submit="handleSubmitReview" @close="handleClose" />
    </div>
    <div v-if="isLoading" class="animate-pulse space-y-4 mb-6 min-h-[400px]">
      <div class="h-32 bg-gray-200 rounded" />
      <div class="h-[300px] bg-gray-200 rounded" />
    </div>
    <div v-else class="mb-6">
      <div v-if="stats.total > 0" class="mb-6">
        <RatingDistributionPanel :ratings_distribution="stats.ratingDistribution" :total_ratings="stats.total"
          :overall_rating="stats.overallRating" single-panel />
      </div>
      <div class="mb-6">
        <AIReviewCard :company-name="props.company_name" :permalink="props.permalink" :role="filters.role"
          :state="filters.state" :city="filters.city" :role-name="filters.roleName || filters.role" :pre-fetched-review="preFetchedAIReview && 'aiReview' in preFetchedAIReview
            ? preFetchedAIReview.aiReview
            : null
            " :filtered="preFetchedAIReview && 'filtered' in preFetchedAIReview
            ? preFetchedAIReview.filtered
            : false
            " :filter-level="preFetchedAIReview
            && preFetchedAIReview.success
            && 'filterLevel' in preFetchedAIReview
            ? preFetchedAIReview.filterLevel
            : ''
            " :is-loading="aiReviewPending" @has-content="updateAIReviewVisibility" />
      </div>
      <div v-if="reviews.length > 0 || userReview" class="mb-6">
        <ReviewsGrid ref="reviewsGridRef" :permalink="props.permalink" :company-name="props.company_name"
          :reviews="mergedReviews" :user-review="userReview" :user-review-id="userReviewId" :is-editable="true"
          :logo-url="companyLogo" @write-review="handleWriteReview" @review-updated="fetchUserReview"
          @edit-review="handleInlineEdit" @delete="handleDeleteReview" />
        <div v-if="!session?.user && reviews.length >= 6" class="mt-6 text-center">
          <Card class="p-6">
            <div class="text-center">
              <h3 class="text-xl font-semibold mb-2">
                Want to see more reviews?
              </h3>
              <p class="text-gray-600 mb-4">
                Sign in to view all {{ stats.total }} reviews for
                {{ props.company_name }}
              </p>
              <Button color="primary" size="lg" @click="handleSignInClick">
                Sign In to View More
              </Button>
            </div>
          </Card>
        </div>
        <div v-if="session?.user && paginatedTotal > itemsPerPage" class="mt-6 flex justify-center">
          <Pagination v-model="currentPage" :page-count="itemsPerPage" :total="paginatedTotal"
            :disabled="isLoadingPagination" show-first show-last />
        </div>
      </div>
    </div>
    <div v-if="citiesData.length > 0 && !filters.city" class="mb-6">
      <SectionHeader :title="dynamicTitle" />
      <CitiesComparisonTable :cities-data="citiesData" />
    </div>
    <AuthModal v-model="showAuthModal" @close="handleAuthModalClose" @signed-in="handleAuthSuccess" />
    <div v-if="!filters.state && !filters.city" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
      <SectionHeader :title="`Latest Job Openings at ${props.company_name}`" class="mb-6">
        <template #action>
          <NuxtLink :to="`/company/${props.permalink}/jobs`" class="hidden sm:block">
            <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
              :brand-color="props.brand_color" />
          </NuxtLink>
        </template>
      </SectionHeader>
      <div class="block sm:hidden mb-4">
        <NuxtLink :to="`/company/${props.permalink}/jobs`">
          <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
            :brand-color="props.brand_color" class="justify-start" />
        </NuxtLink>
      </div>
      <CompanyTopJobs :permalink="props.permalink" :location="''" :company_name="props.company_name"
        :pre-fetched-data="preFetchedJobsData" :is-loading="jobsDataPending" :logo_url="props.logoUrl || companyLogo" />
    </div>
  </div>
</template>
<script setup lang="ts">
defineOptions({
  inheritAttrs: false
})
import { useRoute } from 'vue-router'
import { onMounted, computed, ref, nextTick, inject, watch } from 'vue'
import ActionButton from '../common/ActionButton.vue'
import SectionHeader from '../common/SectionHeader.vue'
import AuthModal from '../AuthModal.vue'
import WriteReviewForm from './review/WriteReviewForm.vue'
import UnifiedFilterBanner from './filters/UnifiedFilterBanner.vue'
import CitiesComparisonTable from './review/CitiesComparisonTable.vue'
import LastUpdatedDate from './LastUpdatedDate.vue'
import CompanyTopJobs from './CompanyTopJobs.vue'
import RatingDistributionPanel from '~/components/common/RatingDistributionPanel.vue'
import AIReviewCard from '~/components/company/review/AIReviewCard.vue'
import ReviewsGrid from '~/components/company/review/ReviewsGrid.vue'
interface Props {
  permalink: string
  company_name: string
  userReview: any
  city?: string | null
  state?: string | null
  initialRole?: string | null
  initialFilters?: any
  preFetchedData?: any
  logoUrl?: string
  brand_color?: string
}
const props = withDefaults(defineProps<Props>(), {
  city: null,
  state: null,
  initialRole: null,
  initialFilters: undefined,
  preFetchedData: undefined,
  logoUrl: '',
  brand_color: '#5F0CE2'
})
const emit = defineEmits(['reviewUpdated', 'filters-updated', 'edit-review'])
interface RatingDistribution {
  1: number
  2: number
  3: number
  4: number
  5: number
}
interface ReviewStats {
  total: number
  ratingDistribution: RatingDistribution
  overallRating: number
}
interface CityData {
  city: string
  state: string
  state_full: string
  reviews_count: number
  avg_rating: number
  rating_distribution: RatingDistribution
  url: string
}
const reviewsData = computed(() => {
  if (props.preFetchedData?.reviewsData) {
    return {
      reviews: props.preFetchedData.reviewsData.reviews || [],
      stats: props.preFetchedData.reviewsData.stats || {
        total: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        overallRating: 0
      }
    }
  }
  return {
    reviews: [],
    stats: {
      total: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      overallRating: 0
    }
  }
})
const citiesResponse = computed(() => {
  return props.preFetchedData?.citiesData || { data: { cities: [] } }
})
const preFetchedAIReview = computed(() => {
  return props.preFetchedData?.aiReviewData || null
})
const aiReviewPending = computed(() => {
  return !props.preFetchedData?.aiReviewData
})
const preFetchedCities = computed(() => {
  if (props.state && props.preFetchedData?.statesCities) {
    const stateCities = props.preFetchedData.statesCities[props.state]
    if (Array.isArray(stateCities)) {
      return stateCities
    }
  }
  if (props.preFetchedData?.citiesByState) {
    return Array.isArray(props.preFetchedData.citiesByState)
      ? props.preFetchedData.citiesByState
      : []
  }
  return []
})
const statesCities = computed(() => {
  if (props.preFetchedData?.statesCities) {
    return props.preFetchedData.statesCities
  }
  return {}
})
const { session } = useUserSession()
const route = useRoute()
const company = inject('companyData', ref({ logo_url: '' }))
const userReview = ref(props.userReview)
const showWriteReview = ref(false)
const writeReviewForm = ref(null)
const writeReviewFormContainer = ref(null)
const isLoading = ref(false)
const isSubmitting = ref(false)
const reviewFormData = ref(null)
const reviewsGridRef = ref(null)
const citiesDisplayContext = ref('')
const showAIReview = ref(true)
const reviews = ref<any[]>(reviewsData.value?.reviews || [])
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalPages = ref(1)
const paginatedTotal = ref(0)
const isLoadingPagination = ref(false)
const showAuthModal = ref(false)
const citiesData = ref<CityData[]>(citiesResponse.value?.data?.cities || [])
const stats = ref<ReviewStats>(
  reviewsData.value?.stats || {
    total: 0,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    overallRating: 0
  }
)
const permalinkRef = ref(props.permalink)
const companyNameRef = ref(props.company_name)
const initialFiltersRef = ref(
  props.initialFilters || {
    role: props.initialRole || '',
    state: props.state || '',
    city: props.city || ''
  }
)
const {
  filters,
  stateFullName,
  locationDisplay,
  handleFilterUpdate: originalHandleFilterUpdate
} = useFilters(initialFiltersRef, permalinkRef)
const mergedReviews = computed(() => {
  let result = [...reviews.value]
  if (!userReview.value) {
    if (!session.value?.user) {
      result = result.slice(0, 6)
    }
    return result
  }
  const userReviewExists = result.some(
    review => review._id === userReview.value._id
  )
  if (!userReviewExists) {
    const enhancedUserReview = {
      ...userReview.value,
      company: {
        permalink: props.permalink,
        brand_name: props.company_name,
        logo_url: companyLogo.value || '/img/logo.svg'
      },
      isUserEditable: true
    }
    result = [enhancedUserReview, ...result]
  } else {
    result = result.map((review) => {
      if (review._id === userReview.value._id) {
        return {
          ...review,
          isUserEditable: true
        }
      }
      return review
    })
    result.sort((a, b) => {
      if (a._id === userReview.value._id) return -1
      if (b._id === userReview.value._id) return 1
      return 0
    })
  }
  return result
})
const userReviewId = computed(() => {
  if (userReview.value && userReview.value._id) {
    return userReview.value._id
  }
  return null
})
async function handleFilter(newFilters) {
  isLoading.value = true
  currentPage.value = 1
  originalHandleFilterUpdate(newFilters)
  await fetchData()
  if (session.value?.user?.id) {
    await fetchUserReview()
  }
  isLoading.value = false
}
const { titleText } = useTitleGenerator(
  filters,
  companyNameRef,
  stateFullName,
  locationDisplay,
  { forReviews: true }
)
const initialRoleForBanner = computed(() => {
  if (props.initialRole?.includes('-')) {
    return props.initialRole.replace(/-/g, ' ')
  }
  return props.initialRole || ''
})
const companyLogo = computed(() => {
  return company.value?.logo_url || '/img/logo.svg'
})
const preFetchedJobsData = computed(() => {
  return props.preFetchedData?.jobsData || { jobs: [], total: 0, available: 0 }
})
const jobsDataPending = computed(() => {
  return !props.preFetchedData?.jobsData
})
async function fetchData() {
  isLoading.value = true
  try {
    const requestBody: any = {
      role: filters.role,
      state: filters.state,
      city: filters.city
    }
    if (session.value?.user) {
      requestBody.page = currentPage.value
      requestBody.limit = itemsPerPage.value
    } else {
      requestBody.limit = 20
    }
    const reviewsResponse = await $fetch(
      `/api/companies/${props.permalink}/reviews/search`,
      {
        method: 'POST',
        body: requestBody
      }
    )
    if (reviewsResponse?.success) {
      if ('reviews' in reviewsResponse) {
        reviews.value = reviewsResponse.reviews || []
      } else {
        reviews.value = []
      }
      if ('stats' in reviewsResponse && reviewsResponse.stats) {
        stats.value = {
          total:
            'total' in reviewsResponse.stats
              ? Number(reviewsResponse.stats.total)
              : 0,
          ratingDistribution: reviewsResponse.stats.ratingDistribution || {
            1: 0,
            2: 0,
            3: 0,
            4: 0,
            5: 0
          },
          overallRating: reviewsResponse.stats.overallRating || 0
        }
      } else {
        stats.value = {
          total: 0,
          ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          overallRating: 0
        }
      }
      if (
        session.value?.user
        && 'pagination' in reviewsResponse
        && reviewsResponse.pagination
      ) {
        const pagination = reviewsResponse.pagination as any
        currentPage.value = pagination.page
        totalPages.value = pagination.pages
        paginatedTotal.value = pagination.total
      }
    } else {
      reviews.value = []
      stats.value = {
        total: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        overallRating: 0
      }
      paginatedTotal.value = 0
    }
    const citiesResponse = await $fetch<{
      success: boolean
      data: { cities: CityData[], display_context?: string }
    }>(`/api/companies/${props.permalink}/reviews/search`, {
      method: 'POST',
      body: {
        mode: 'cities-comparison',
        role: filters.role,
        state: filters.state,
        city: filters.city
      }
    })
    if (
      citiesResponse.success
      && citiesResponse.data
      && citiesResponse.data.cities
    ) {
      citiesData.value = citiesResponse.data.cities
      if (citiesResponse.data.display_context) {
        citiesDisplayContext.value = citiesResponse.data.display_context
      }
    } else {
      citiesData.value = []
      citiesDisplayContext.value = ''
    }
  } catch (error) {
    reviews.value = []
    stats.value = {
      total: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      overallRating: 0
    }
    paginatedTotal.value = 0
    citiesData.value = []
    citiesDisplayContext.value = ''
  } finally {
    isLoading.value = false
  }
}
function updateAIReviewVisibility(hasContent) {
  showAIReview.value = hasContent
}
function handleWriteReview() {
  if (!session.value?.user) {
    localStorage.setItem('redirect_uri', route.fullPath)
    showAuthModal.value = true
    return
  }
  if (!userReview.value) {
    reviewFormData.value = null
    showWriteReview.value = true
    nextTick(() => {
      if (writeReviewFormContainer.value) {
        window.scrollTo({
          top: writeReviewFormContainer.value.offsetTop - 100,
          behavior: 'smooth'
        })
      }
    })
  } else if (userReview.value && reviewsGridRef.value && userReviewId.value) {
    showWriteReview.value = false
    nextTick(() => {
      reviewsGridRef.value.startEditing(userReviewId.value)
      const gridEl = document.querySelector('.full-width-edit-container')
      if (gridEl) {
        gridEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  }
}
function handleInlineEdit(reviewId) {
  if (userReview.value && userReview.value._id === reviewId) {
    if (reviewsGridRef.value) {
      reviewsGridRef.value.startEditing(reviewId)
    } else {
      showWriteReview.value = true
      reviewFormData.value = userReview.value
    }
  }
}
async function handleDeleteReview(reviewId) {
  if (!session.value?.user?.id) return
  try {
    const response = await $fetch(
      `/api/user/reviews/${session.value.user.id}/${reviewId}.delete`,
      {
        method: 'DELETE'
      }
    )
    if (response.success) {
      const toast = useToast()
      toast.add({
        title: 'Success',
        description: 'Your review has been deleted',
        color: 'green'
      })
      userReview.value = null
      emit('reviewUpdated')
      await fetchData()
    }
  } catch (error) {
    const toast = useToast()
    toast.add({
      title: 'Error',
      description: error.data?.message || 'Failed to delete review',
      color: 'red'
    })
  }
}
async function handleSubmitReview(formData) {
  isSubmitting.value = true
  try {
    const reviewData = {
      ...formData,
      rating_details: formData.rating_details || {},
      user_id: session.value?.user?.id
    }
    if (userReview.value && userReview.value._id) {
      reviewData.review_id = userReview.value._id
    }
    const endpoint = userReview.value
      ? `/api/companies/${props.permalink}/reviews/update`
      : `/api/companies/${props.permalink}/reviews/submit`
    const response = await $fetch<{ success: boolean, message?: string }>(
      endpoint,
      {
        method: 'POST',
        body: reviewData
      }
    )
    if (response.success) {
      writeReviewForm.value?.handleSubmissionResponse(true, response.message)
      emit('reviewUpdated')
      await fetchUserReview()
      await fetchData()
      showWriteReview.value = false
    }
  } catch (error) {
    const errorMessage
      = error.data?.message || 'Failed to submit review. Please try again.'
    writeReviewForm.value?.handleSubmissionResponse(false, errorMessage)
  } finally {
    isSubmitting.value = false
  }
}
function handleClose() {
  showWriteReview.value = false
  reviewFormData.value = null
  emit('reviewUpdated')
}
async function fetchUserReview() {
  if (!session.value?.user?.id) return
  try {
    const response = await $fetch<{ success: boolean, review?: any }>(
      `/api/companies/${props.permalink}/reviews/user/${session.value.user.id}`
    )
    if (response?.success && response.review) {
      userReview.value = {
        ...response.review,
        company: {
          permalink: props.permalink,
          brand_name: props.company_name,
          logo_url: companyLogo.value
        }
      }
      emit('reviewUpdated')
    } else {
      userReview.value = null
    }
  } catch (error) {
    console.error('Error fetching user review:', error)
    userReview.value = null
  }
}
const dynamicTitle = computed(() => {
  let title = `${props.company_name} Employee Reviews`
  if (filters.role) {
    const roleDisplay = filters.roleName || filters.role
    title += ` for ${roleDisplay} by Location`
  } else {
    title += ` by Location`
  }
  if (filters.city && filters.state) {
    title += ` in ${filters.city}, ${filters.state}`
  } else if (stateFullName.value) {
    title += ` in ${stateFullName.value}`
  }
  return title
})
function handleSignInClick() {
  localStorage.setItem('redirect_uri', route.fullPath)
  showAuthModal.value = true
}
function handleAuthModalClose() {
  showAuthModal.value = false
}
function handleAuthSuccess() {
  showAuthModal.value = false
  window.location.reload()
}
async function handlePageChange(newPage: number) {
  if (newPage === currentPage.value) return
  isLoadingPagination.value = true
  currentPage.value = newPage
  try {
    await fetchData()
  } finally {
    isLoadingPagination.value = false
  }
}
watch(currentPage, async (newPage, oldPage) => {
  if (newPage !== oldPage) {
    isLoadingPagination.value = true
    try {
      await fetchData()
    } finally {
      isLoadingPagination.value = false
    }
  }
})
onMounted(async () => {
  if (session.value?.user?.id) {
    await fetchUserReview()
    await fetchData()
  } else {
    if (props.preFetchedData) {
      if (props.preFetchedData.reviewsData?.reviews) {
        reviews.value = props.preFetchedData.reviewsData.reviews
      }
      if (props.preFetchedData.reviewsData?.stats) {
        stats.value = props.preFetchedData.reviewsData.stats
      }
      if (props.preFetchedData.citiesData?.data?.cities) {
        citiesData.value = props.preFetchedData.citiesData.data.cities
      }
    }
  }
  if (route.query.edit === 'true' && userReview.value) {
    showWriteReview.value = true
  }
})
</script>